import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Share2, Link, MessageCircle, Facebook, Instagram } from 'lucide-react';
import { truncate } from '@/seo/config';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL;

const ShareButton = ({ post, onShare }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Backend-rendered preview page: real OG title/image for WhatsApp & Facebook, redirects humans to /post/:id
  const shareUrl = `${API_URL}/api/share/post/${post.post_id}`;
  const shareText = `${post.title} – ${truncate(post.content, 100)}`;
  const siteHost = (SITE_URL || window.location.origin).replace(/^https?:\/\//, '');

  const done = () => { onShare?.(); setIsOpen(false); };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      if (!ok) toast.error('Erreur lors de la copie');
      return ok;
    }
  };

  const handleCopyLink = async () => {
    if (await copy(shareUrl)) toast.success('Lien copié !');
    done();
  };

  const handleWhatsAppShare = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`, '_blank', 'noopener');
    done();
  };

  const handleFacebookShare = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400,noopener');
    done();
  };

  const handleInstagramShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: shareText, url: shareUrl });
        done();
        return;
      } catch (error) {
        if (error.name === 'AbortError') { setIsOpen(false); return; }
      }
    }
    if (await copy(`${shareText}\n\n${shareUrl}`)) {
      toast.success('Texte et lien copiés ! Collez-les dans votre story ou publication Instagram.');
      window.open('https://www.instagram.com/', '_blank', 'noopener');
    }
    done();
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: post.title, text: shareText, url: shareUrl });
      done();
    } catch (error) {
      if (error.name !== 'AbortError') toast.error('Erreur lors du partage');
      setIsOpen(false);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          data-testid={`share-btn-${post.post_id}`}
        >
          <Share2 className="h-4 w-4" />
          <span className="hidden sm:inline">{post.shares_count || 0}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-2">
        <div className="rounded-lg border border-border overflow-hidden bg-muted/30 mb-2" data-testid={`share-preview-${post.post_id}`}>
          {post.image_url && (
            <img src={post.image_url} alt="" className="w-full h-28 object-cover" loading="lazy" />
          )}
          <div className="p-2.5">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{siteHost}</p>
            <p className="text-sm font-semibold leading-tight line-clamp-2">{post.title}</p>
            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{truncate(post.content, 90)}</p>
          </div>
        </div>
        <DropdownMenuItem onClick={handleWhatsAppShare} className="cursor-pointer" data-testid={`share-whatsapp-${post.post_id}`}>
          <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleFacebookShare} className="cursor-pointer" data-testid={`share-facebook-${post.post_id}`}>
          <Facebook className="h-4 w-4 mr-2 text-blue-600" />
          Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleInstagramShare} className="cursor-pointer" data-testid={`share-instagram-${post.post_id}`}>
          <Instagram className="h-4 w-4 mr-2 text-pink-500" />
          Instagram
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer" data-testid={`share-copy-${post.post_id}`}>
          <Link className="h-4 w-4 mr-2" />
          Copier le lien
        </DropdownMenuItem>
        {navigator.share && (
          <DropdownMenuItem onClick={handleNativeShare} className="cursor-pointer" data-testid={`share-native-${post.post_id}`}>
            <Share2 className="h-4 w-4 mr-2" />
            Plus d'options...
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareButton;
